import BaseApiService from './BaseApiService'

export type ControlStatus = 'green' | 'amber' | 'red'

export interface ComplianceControlRow {
  controlKey: string
  title: string
  description: string
  criteria: string
  frequency: string
  kind: 'manual' | 'automated'
  docPath: string | null
  evidenceHint: string | null
  status: ControlStatus
  latestRun: { result: string; summary: string; finishedAt: string; source: string } | null
  openTask: { complianceTaskId: string; periodKey: string; dueAt: string } | null
}

export interface ComplianceDashboard {
  controls: ComplianceControlRow[]
  openTasks: number
  overdueTasks: number
  failingChecks: number
}

export interface ComplianceTask {
  complianceTaskId: string
  controlKey: string
  controlTitle: string | null
  periodKey: string
  dueAt: string
  status: 'pending' | 'done' | 'waived'
  completedAt: string | null
  notes: string | null
  evidenceFileName: string | null
}

class ComplianceApiService extends BaseApiService {
  dashboard() {
    return this.post<ComplianceDashboard>('/v1/admin/compliance-center/dashboard')
  }

  tasks(status?: 'pending' | 'done' | 'waived') {
    return this.post<ComplianceTask[]>('/v1/admin/compliance-center/tasks', { status: status ?? null })
  }

  complete(taskId: string, notes: string, evidence?: File | null) {
    const form = new FormData()
    form.append('taskId', taskId)
    form.append('notes', notes)
    if (evidence) form.append('evidence', evidence)
    return this.postForm<{ success: boolean }>('/v1/admin/compliance-center/tasks/complete', form)
  }

  waive(taskId: string, reason: string) {
    return this.post<{ success: boolean }>('/v1/admin/compliance-center/tasks/waive', { taskId, reason })
  }

  /** Audit package (zip): catalog, verified compliance log, check runs, tasks, evidence files, digest manifest. */
  exportPackage(since: string, until: string) {
    return this.request<Blob>('POST', '/v1/admin/compliance-center/export-package', { since, until }, true)
  }

  evidence(taskId: string) {
    return this.request<Blob>('POST', '/v1/admin/compliance-center/tasks/evidence', { taskId }, true)
  }
}

export const complianceApiService = new ComplianceApiService()
